import { useMemo } from 'react'
import { isImageName, matchesSlot } from './use-attachment-draft'
import type { AttachmentDraft, DraftItem } from './use-attachment-draft'
import type { GrowerFileKind, GrowerFileRecord } from '@/lib/api/growers-api'
import { fileDownloadUrl } from '@/lib/api/files-api'
import { myGrowerFileDownloadUrl } from '@/lib/api/my-grower-api'

/**
 * A frozen AttachmentDraft over a household's saved documents, so a read-only
 * screen can reuse GrowerAttachmentsField as-is. The mutating half of the draft
 * contract is inert — these surfaces have no save path at all.
 *
 * `scope` chọn route tải tệp; dùng nhầm là ảnh giấy tờ hiện thành ảnh vỡ:
 * - 'household' — hộ tự xem hồ sơ của mình, đi
 *   `/care-logs/my-growers/:id/files/:fileId/download`. Tài khoản hộ không có
 *   `files.read` nên bắt buộc phải qua route riêng này.
 * - 'admin' — nhân viên xem trang chi tiết hộ, đi `/files/:id/download`. Route
 *   của hộ chỉ nhận đúng hộ đang đăng nhập nên trả 404 cho nhân viên.
 *
 * `areaId` lọc thêm theo vùng trồng — giấy tờ đất thuộc từng vùng nên mỗi tab
 * vùng chỉ hiện tệp của nó. Bỏ trống = ô của cả hộ (giấy tờ hồ sơ).
 */
export function useReadonlyAttachments(
    growerId: string,
    files: GrowerFileRecord[] | undefined,
    kind: GrowerFileKind = 'profile',
    scope: 'household' | 'admin' = 'household',
    areaId?: string,
): AttachmentDraft {
    return useMemo(() => {
        const items: DraftItem[] = (files ?? [])
            .filter((f) => matchesSlot(f, kind, areaId))
            .map((f) => ({
                key: `ex-${f.id}`,
                name: f.name ?? f.id,
                url:
                    scope === 'admin'
                        ? fileDownloadUrl(f.id)
                        : myGrowerFileDownloadUrl(growerId, f.id),
                isImage: isImageName(f.name),
                kind: 'existing' as const,
                fileId: f.id,
            }))
        return {
            items,
            addFiles: () => {},
            remove: () => {},
            commit: async () => {},
            hasChanges: false,
        }
    }, [growerId, files, kind, scope, areaId])
}
